import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { 
  FaCheck, 
  FaTimes, 
  FaEye, 
  FaEyeSlash,
  FaExclamationCircle,
  FaUser,
  FaBuilding
} from 'react-icons/fa'; 
import apiClient from '../../api';
import { Button } from '../../components/common/Button';
import { FormGroup, Label, Input, Message } from '../../components/common/Forms';
import { AuthPageContainer, AuthFormCard, Title } from '../../components/common/AuthLayout';
import Header from '../../components/layout/Header'; 
import Footer from '../../components/layout/Footer';

// --- Styled Components Existentes ---
const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100vh;
`;

const ContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;

const Row = styled.div`
  display: flex;
  gap: 1rem;
  @media (max-width: 600px) {
    flex-direction: column;
    gap: 0;
  }
  & > ${FormGroup} {
    flex: 1;
  }
`;

const PhoneContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`;

const SelectDDI = styled.select`
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 8px;
  background-color: #fff;
  font-family: inherit;
  font-size: 1rem;
  width: 90px;
  cursor: pointer;
  height: 48px;

  &:focus {
    border-color: ${({ theme }) => theme.colors.primary};
    outline: none;
  }
`;

const InputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;

  input {
    padding-right: 40px;
    ${props => props.hasError && css`
      border-color: #dc3545 !important;
      &:focus {
        box-shadow: 0 0 0 2px rgba(220, 53, 69, 0.2);
      }
    `}
    ${props => props.isValid && !props.hasError && css`
      border-color: #28a745 !important;
    `}
  }
`;

const IconContainer = styled.div`
  position: absolute;
  right: 10px;
  display: flex;
  align-items: center;
  color: ${props => props.color || '#666'};
  pointer-events: none;
  
  ${props => props.isButton && css`
    pointer-events: auto;
    cursor: pointer;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
  `}
`;

const ErrorText = styled.small`
  color: #dc3545;
  font-size: 0.75rem;
  margin-top: 0.25rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const ChecklistContainer = styled.div`
  margin-top: 0.5rem;
  background-color: #f9f9f9;
  padding: 0.8rem;
  border-radius: 6px;
  border: 1px solid #eee;
`;

const ChecklistItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  margin-bottom: 0.25rem;
  color: ${props => props.isValid ? '#28a745' : '#dc3545'}; 
  transition: color 0.2s ease;

  svg { font-size: 0.7rem; }
  &:last-child { margin-bottom: 0; }
`;

// --- Novos Styled Components para o Checkbox ---

const CheckboxWrapper = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 0.5rem;
  cursor: pointer;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  user-select: none;
`;

const HiddenCheckbox = styled.input.attrs({ type: 'checkbox' })`
  border: 0;
  clip: rect(0 0 0 0);
  height: 1px;
  margin: -1px;
  overflow: hidden;
  padding: 0;
  position: absolute;
  white-space: nowrap;
  width: 1px;
`;

const StyledCheckbox = styled.div`
  display: inline-block;
  width: 18px;
  height: 18px;
  background: ${props => (props.checked ? '#28a745' : '#fff')};
  border: 2px solid ${props => (props.hasError ? '#dc3545' : props.checked ? '#28a745' : '#ccc')};
  border-radius: 4px;
  transition: all 150ms;
  flex-shrink: 0;
  position: relative;
  margin-top: 2px; // Alinha visualmente com a primeira linha do texto

  ${HiddenCheckbox}:focus + & {
    box-shadow: 0 0 0 2px rgba(40, 167, 69, 0.2);
  }

  &::after {
    content: '';
    position: absolute;
    display: ${props => (props.checked ? 'block' : 'none')};
    left: 4px;
    top: 0px;
    width: 5px;
    height: 10px;
    border: solid white;
    border-width: 0 2px 2px 0;
    transform: rotate(45deg);
  }
`;

const LabelText = styled.span`
  font-size: 0.9rem;
  color: #555;
  line-height: 1.4;

  a {
    color: ${({ theme }) => theme.colors.primary || '#007bff'};
    text-decoration: none;
    font-weight: 500;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const TypeSelectorContainer = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  justify-content: center;
`;

const TypeOption = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.5rem;
  border: 2px solid ${props => props.selected ? props.theme.colors.primary : '#eee'};
  background-color: ${props => props.selected ? `${props.theme.colors.primary}10` : '#fff'};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  font-weight: 500;
  color: ${props => props.selected ? props.theme.colors.primary : '#666'};

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
`;

const isValidCPF = (cpf) => {
    if (!cpf) return false;
    // Remove tudo que não for dígito para validar
    const cleanCpf = cpf.replace(/[^\d]+/g, '');
    
    if (cleanCpf.length !== 11 || !!cleanCpf.match(/(\d)\1{10}/)) return false;
    
    const cpfArr = cleanCpf.split('').map(el => +el);
    const rest = (count) => (cpfArr.slice(0, count - 12).reduce((soma, el, index) => (soma + el * (count - index)), 0) * 10) % 11 % 10;
    return rest(10) === cpfArr[9] && rest(11) === cpfArr[10];
};

const isValidCNPJ = (cnpj) => {
    if (!cnpj) return false;
    // Remove pontuação para contar os dígitos
    const cleanCnpj = cnpj.replace(/[^\d]+/g, '');
    
    // Valida apenas se tem 14 números (após limpeza)
    if (cleanCnpj.length !== 14) return false;
    if (/^(\d)\1+$/.test(cleanCnpj)) return false; 
    return true; 
};

const countryCodes = [
  { code: '+55', country: 'BR', label: '🇧🇷 +55' },
  { code: '+1', country: 'US', label: '🇺🇸 +1' },
  { code: '+351', country: 'PT', label: '🇵🇹 +351' },
  { code: '+44', country: 'UK', label: '🇬🇧 +44' },
  { code: '+54', country: 'AR', label: '🇦🇷 +54' },
];

const CompletarCadastroPage = () => {
  const { token } = useParams(); 
  const navigate = useNavigate();

  // Estados
  const [tipoPessoa, setTipoPessoa] = useState('PJ');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [nomeEmpresa, setNomeEmpresa] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [cpf, setCpf] = useState('');
  const [ddi, setDdi] = useState('+55'); 
  const [telefoneNumero, setTelefoneNumero] = useState('');
  const [inscricaoEstadual, setInscricaoEstadual] = useState('');
  const [inscricaoMunicipal, setInscricaoMunicipal] = useState(''); 
  const [emailCobranca, setEmailCobranca] = useState(''); 
  const [cep, setCep] = useState('');
  const [logradouro, setLogradouro] = useState('');
  const [numero, setNumero] = useState('');
  const [complemento, setComplemento] = useState('');
  const [bairro, setBairro] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  
  // Novo estado para os termos
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const [errors, setErrors] = useState({});
  const [mensagem, setMensagem] = useState({ text: '', type: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const MAX_PASSWORD_LENGTH = 32;
  const MAX_PHONE_LENGTH = 15;

  // Validação Senha
  const passwordValidations = useMemo(() => {
    return {
      length: senha.length >= 8 && senha.length <= MAX_PASSWORD_LENGTH,
      hasLetter: /[a-zA-Z]/.test(senha),
      hasNumber: /[0-9]/.test(senha),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(senha)
    };
  }, [senha]);

  const isPasswordValid = Object.values(passwordValidations).every(Boolean);

  const handleNumberInput = (setter, fieldName, limit = null) => (e) => {
    let value = e.target.value.replace(/\D/g, '');
    if (limit && value.length > limit) value = value.slice(0, limit);
    setter(value);
    if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
  };

  const handleTextInput = (setter, fieldName) => (e) => {
    setter(e.target.value);
    if (errors[fieldName]) setErrors(prev => ({ ...prev, [fieldName]: null }));
  };

  const handleCheckboxChange = (e) => {
    setTermsAccepted(e.target.checked);
    if (errors.terms) setErrors(prev => ({ ...prev, terms: null }));
  };

  // --- Validação Geral ---
  const validateForm = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!isPasswordValid) newErrors.senha = 'A senha não atende aos requisitos.';
    if (senha !== confirmarSenha) newErrors.confirmarSenha = 'As senhas não conferem.';
    if (tipoPessoa === 'PJ') {
        if (!nomeEmpresa.trim()) newErrors.nomeEmpresa = 'Razão Social é obrigatória.';
        if (!isValidCNPJ(cnpj)) newErrors.cnpj = 'CNPJ inválido.';
    } else {
        if (!cpf) newErrors.cpf = 'CPF é obrigatório.';
        else if (!isValidCPF(cpf)) newErrors.cpf = 'CPF inválido.';
    }
    if (telefoneNumero.length < 8 || telefoneNumero.length > MAX_PHONE_LENGTH) {
        newErrors.telefone = 'Telefone inválido (entre 8 e 15 dígitos).';
    }
    if (emailCobranca && !emailRegex.test(emailCobranca)) newErrors.emailCobranca = 'E-mail inválido.';
    if (cep.length !== 8) newErrors.cep = 'CEP deve ter 8 dígitos.';
    if (!logradouro.trim()) newErrors.logradouro = 'Logradouro obrigatório.';
    if (!numero.trim()) newErrors.numero = 'Número obrigatório.';
    if (!bairro.trim()) newErrors.bairro = 'Bairro obrigatório.';
    if (!cidade.trim()) newErrors.cidade = 'Cidade obrigatória.';
    if (!estado.trim()) newErrors.estado = 'Estado obrigatório.';

    // Validação dos Termos
    if (!termsAccepted) {
        newErrors.terms = 'Você deve aceitar os termos para continuar.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCepBlur = async () => {
    if (cep.length !== 8) return;
    try {
      const response = await fetch(`https://viacep.com.br/ws/${cep}/json/`);
      const data = await response.json();
      if (!data.erro) {
        setLogradouro(data.logradouro);
        setBairro(data.bairro);
        setCidade(data.localidade);
        setEstado(data.uf);
        setErrors(prev => ({ ...prev, logradouro: null, bairro: null, cidade: null, estado: null }));
      }
    } catch (error) {
      console.error("Erro CEP:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMensagem({ text: '', type: '' });

    if (!validateForm()) {
        setMensagem({ text: 'Por favor, corrija os erros destacados.', type: 'error' });
        return;
    }

    setIsLoading(true);
    const telefoneCompleto = `${ddi}${telefoneNumero}`;

    const payload = {
        token,
        senha,
        confirmar_senha: confirmarSenha,
        tipo_pessoa: tipoPessoa,
        telefone: telefoneCompleto, 
        endereco_cep: cep,
        endereco_logradouro: logradouro,
        endereco_numero: numero,
        endereco_complemento: complemento || null,
        endereco_bairro: bairro,
        endereco_cidade: cidade,
        endereco_estado: estado,
        inscricao_municipal: inscricaoMunicipal || null,
        email_cobranca: emailCobranca || null,
        termos_aceitos: true,
        data_aceite: new Date().toISOString()
    };

    if (tipoPessoa === 'PJ') {
        payload.nome_empresa = nomeEmpresa;
        payload.cnpj = cnpj;
        payload.inscricao_estadual = inscricaoEstadual || null;
    } else {
        payload.cpf = cpf;
        // Para PF, não enviamos nome_empresa ou IE
    }

    try {
      const response = await apiClient.post('/api/completar-cadastro', payload);

      setMensagem({ text: response.data.message, type: 'success' });
      setTimeout(() => {
        navigate('/login');
      }, 3000);

    } catch (error) {
      setMensagem({
        text: error.response?.data?.error || 'Erro ao completar cadastro.',
        type: 'error',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const StatusIcon = ({ hasError, isValid }) => {
    if (hasError) return <FaExclamationCircle color="#dc3545" />;
    if (isValid) return <FaCheck color="#28a745" />;
    return null;
  };

  return (
    <PageWrapper>
      <Header />
      <ContentWrapper>
        <AuthPageContainer>
          <AuthFormCard style={{ maxWidth: '700px' }}>
            <Title>Complete seu Cadastro</Title>
            <TypeSelectorContainer>
                <TypeOption 
                    selected={tipoPessoa === 'PF'} 
                    onClick={() => { setTipoPessoa('PF'); setErrors({}); }}
                >
                    <FaUser /> Pessoa Física
                </TypeOption>
                <TypeOption 
                    selected={tipoPessoa === 'PJ'} 
                    onClick={() => { setTipoPessoa('PJ'); setErrors({}); }}
                >
                    <FaBuilding /> Pessoa Jurídica (Empresa)
                </TypeOption>
            </TypeSelectorContainer>
            
            <form onSubmit={handleSubmit} noValidate>
              
              {/* --- SENHA --- */}
              <Row>
                <FormGroup>
                  <Label htmlFor="senha">Crie sua Senha</Label>
                  <InputWrapper hasError={!!errors.senha} isValid={isPasswordValid && senha.length > 0}>
                    <Input 
                      id="senha" 
                      type={showPassword ? "text" : "password"} 
                      value={senha} 
                      onChange={e => setSenha(e.target.value)} 
                      maxLength={MAX_PASSWORD_LENGTH}
                    />
                    <IconContainer isButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </IconContainer>
                  </InputWrapper>
                  
                  <ChecklistContainer>
                    <ChecklistItem isValid={passwordValidations.length}>
                      {passwordValidations.length ? <FaCheck /> : <FaTimes />} 8 a {MAX_PASSWORD_LENGTH} caracteres
                    </ChecklistItem>
                    <ChecklistItem isValid={passwordValidations.hasLetter}>
                      {passwordValidations.hasLetter ? <FaCheck /> : <FaTimes />} Pelo menos uma letra
                    </ChecklistItem>
                    <ChecklistItem isValid={passwordValidations.hasNumber}>
                      {passwordValidations.hasNumber ? <FaCheck /> : <FaTimes />} Pelo menos um número
                    </ChecklistItem>
                    <ChecklistItem isValid={passwordValidations.hasSpecial}>
                      {passwordValidations.hasSpecial ? <FaCheck /> : <FaTimes />} Um caractere especial (!@#$...)
                    </ChecklistItem>
                  </ChecklistContainer>
                  {errors.senha && <ErrorText>{errors.senha}</ErrorText>}
                </FormGroup>

                <FormGroup>
                  <Label htmlFor="confirmarSenha">Confirme sua Senha</Label>
                  <InputWrapper hasError={!!errors.confirmarSenha} isValid={confirmarSenha && senha === confirmarSenha}>
                    <Input 
                      id="confirmarSenha" 
                      type={showConfirmPassword ? "text" : "password"} 
                      value={confirmarSenha} 
                      onChange={handleTextInput(setConfirmarSenha, 'confirmarSenha')} 
                      maxLength={MAX_PASSWORD_LENGTH}
                    />
                    <IconContainer isButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                      {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </IconContainer>
                  </InputWrapper>
                  {errors.confirmarSenha && <ErrorText>{errors.confirmarSenha}</ErrorText>}
                </FormGroup>
              </Row>
              
              <hr style={{margin: '1rem 0', border: 'none', borderTop: '1px solid #eee'}} />

              {tipoPessoa === 'PJ' ? (
                <Row>
                  <FormGroup style={{ flex: 2 }}>
                    <Label htmlFor="nomeEmpresa">Razão Social</Label>
                    <InputWrapper hasError={!!errors.nomeEmpresa} isValid={!!nomeEmpresa}>
                      <Input 
                        id="nomeEmpresa" 
                        type="text" 
                        value={nomeEmpresa} 
                        onChange={e => {
                            setNomeEmpresa(e.target.value);
                            if(errors.nomeEmpresa) setErrors({...errors, nomeEmpresa: null});
                        }} 
                      />
                      <IconContainer><StatusIcon hasError={!!errors.nomeEmpresa} isValid={!!nomeEmpresa} /></IconContainer>
                    </InputWrapper>
                    {errors.nomeEmpresa && <ErrorText>{errors.nomeEmpresa}</ErrorText>}
                  </FormGroup>

                  <FormGroup style={{ flex: 1 }}>
                    <Label htmlFor="cnpj">CNPJ</Label>
                    <InputWrapper hasError={!!errors.cnpj} isValid={isValidCNPJ(cnpj)}>
                      {/* REMOVIDO: maxLength e handleNumberInput. ADICIONADO: onChange simples */}
                      <Input 
                        id="cnpj" 
                        type="text" 
                        value={cnpj} 
                        onChange={e => {
                            setCnpj(e.target.value);
                            if(errors.cnpj) setErrors({...errors, cnpj: null});
                        }} 
                        placeholder="00.000.000/0001-00"
                      />
                      <IconContainer><StatusIcon hasError={!!errors.cnpj} isValid={isValidCNPJ(cnpj)} /></IconContainer>
                    </InputWrapper>
                    {errors.cnpj && <ErrorText>{errors.cnpj}</ErrorText>}
                  </FormGroup>
                </Row>
            ) : (
                <Row>
                  <FormGroup style={{ flex: 1 }}>
                    <Label htmlFor="cpf">CPF</Label>
                    <InputWrapper hasError={!!errors.cpf} isValid={isValidCPF(cpf)}>
                      {/* REMOVIDO: maxLength e handleNumberInput. ADICIONADO: onChange simples */}
                      <Input 
                        id="cpf" 
                        type="text" 
                        value={cpf} 
                        onChange={e => {
                            setCpf(e.target.value);
                            if(errors.cpf) setErrors({...errors, cpf: null});
                        }} 
                        placeholder="000.000.000-00"
                      />
                      <IconContainer><StatusIcon hasError={!!errors.cpf} isValid={isValidCPF(cpf)} /></IconContainer>
                    </InputWrapper>
                    {errors.cpf && <ErrorText>{errors.cpf}</ErrorText>}
                  </FormGroup>
                  
                  {/* Espaço vazio para manter grid balanceado */}
                  <FormGroup style={{ flex: 1, visibility: 'hidden' }}><Input /></FormGroup>
                </Row>
            )}
              
              {/* --- TELEFONE --- */}
              <Row>
                <FormGroup>
                  <Label htmlFor="telefone">Telefone (WhatsApp)</Label>
                  <PhoneContainer>
                    <SelectDDI value={ddi} onChange={e => setDdi(e.target.value)}>
                        {countryCodes.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                    </SelectDDI>
                    <InputWrapper style={{flex: 1}} hasError={!!errors.telefone} isValid={telefoneNumero.length >= 8}>
                        <Input 
                            id="telefone" 
                            type="tel" 
                            value={telefoneNumero} 
                            onChange={e => {
                                const val = e.target.value.replace(/[^\d\s()-]/g, '');
                                setTelefoneNumero(val);
                                if(errors.telefone) setErrors({...errors, telefone: null});
                            }}
                            placeholder="DDD + Número"
                        />
                         <IconContainer><StatusIcon hasError={!!errors.telefone} isValid={telefoneNumero.length >= 8} /></IconContainer>
                    </InputWrapper>
                  </PhoneContainer>
                  {errors.telefone && <ErrorText>{errors.telefone}</ErrorText>}
                </FormGroup>

                {tipoPessoa === 'PJ' && (
                    <FormGroup>
                    <Label htmlFor="ie">Inscrição Estadual (Opcional)</Label>
                    <InputWrapper isValid={!!inscricaoEstadual}>
                        <Input id="ie" type="text" value={inscricaoEstadual} onChange={e => setInscricaoEstadual(e.target.value)} />
                    </InputWrapper>
                    </FormGroup>
                )}
              </Row>

              <Row>
                <FormGroup>
                  <Label htmlFor="im">Inscrição Municipal (Opcional)</Label>
                  <InputWrapper isValid={!!inscricaoMunicipal}>
                    <Input id="im" type="text" value={inscricaoMunicipal} onChange={e => setInscricaoMunicipal(e.target.value)} />
                    {inscricaoMunicipal && <IconContainer><FaCheck color="#28a745" /></IconContainer>}
                  </InputWrapper>
                </FormGroup>
                
                <FormGroup>
                  <Label htmlFor="emailCobranca">Email de Cobrança (Opcional)</Label>
                  <InputWrapper hasError={!!errors.emailCobranca} isValid={emailCobranca && !errors.emailCobranca}>
                    <Input id="emailCobranca" type="email" value={emailCobranca} onChange={handleTextInput(setEmailCobranca, 'emailCobranca')} />
                    <IconContainer><StatusIcon hasError={!!errors.emailCobranca} isValid={emailCobranca && !errors.emailCobranca} /></IconContainer>
                  </InputWrapper>
                  {errors.emailCobranca && <ErrorText>{errors.emailCobranca}</ErrorText>}
                </FormGroup>
              </Row>
              
              <hr style={{margin: '1rem 0', border: 'none', borderTop: '1px solid #eee'}} />

              {/* --- ENDEREÇO --- */}
              <Row>
                <FormGroup style={{ flex: 1 }}>
                  <Label htmlFor="cep">CEP (Apenas números)</Label>
                  <InputWrapper hasError={!!errors.cep} isValid={cep.length === 8}>
                    <Input id="cep" type="text" value={cep} onChange={handleNumberInput(setCep, 'cep', 8)} onBlur={handleCepBlur} maxLength={8} />
                    <IconContainer><StatusIcon hasError={!!errors.cep} isValid={cep.length === 8} /></IconContainer>
                  </InputWrapper>
                  {errors.cep && <ErrorText>{errors.cep}</ErrorText>}
                </FormGroup>
                
                <FormGroup style={{ flex: 3 }}>
                  <Label htmlFor="logradouro">Logradouro</Label>
                  <InputWrapper hasError={!!errors.logradouro} isValid={!!logradouro}>
                    <Input id="logradouro" type="text" value={logradouro} onChange={handleTextInput(setLogradouro, 'logradouro')} />
                    <IconContainer><StatusIcon hasError={!!errors.logradouro} isValid={!!logradouro} /></IconContainer>
                  </InputWrapper>
                  {errors.logradouro && <ErrorText>{errors.logradouro}</ErrorText>}
                </FormGroup>
              </Row>

              <Row>
                <FormGroup style={{ flex: 1 }}>
                  <Label htmlFor="numero">Número</Label>
                  <InputWrapper hasError={!!errors.numero} isValid={!!numero}>
                    <Input id="numero" type="text" value={numero} onChange={handleNumberInput(setNumero, 'numero', 10)} />
                    <IconContainer><StatusIcon hasError={!!errors.numero} isValid={!!numero} /></IconContainer>
                  </InputWrapper>
                  {errors.numero && <ErrorText>{errors.numero}</ErrorText>}
                </FormGroup>
                
                <FormGroup style={{ flex: 1 }}>
                  <Label htmlFor="complemento">Complemento (Opcional)</Label>
                  <InputWrapper isValid={!!complemento}>
                    <Input id="complemento" type="text" value={complemento} onChange={e => setComplemento(e.target.value)} />
                    {complemento && <IconContainer><FaCheck color="#28a745" /></IconContainer>}
                  </InputWrapper>
                </FormGroup>
                
                <FormGroup style={{ flex: 2 }}>
                  <Label htmlFor="bairro">Bairro</Label>
                  <InputWrapper hasError={!!errors.bairro} isValid={!!bairro}>
                    <Input id="bairro" type="text" value={bairro} onChange={handleTextInput(setBairro, 'bairro')} />
                    <IconContainer><StatusIcon hasError={!!errors.bairro} isValid={!!bairro} /></IconContainer>
                  </InputWrapper>
                  {errors.bairro && <ErrorText>{errors.bairro}</ErrorText>}
                </FormGroup>
              </Row>

              <Row>
                <FormGroup style={{ flex: 3 }}>
                  <Label htmlFor="cidade">Cidade</Label>
                  <InputWrapper hasError={!!errors.cidade} isValid={!!cidade}>
                    <Input id="cidade" type="text" value={cidade} onChange={handleTextInput(setCidade, 'cidade')} />
                    <IconContainer><StatusIcon hasError={!!errors.cidade} isValid={!!cidade} /></IconContainer>
                  </InputWrapper>
                  {errors.cidade && <ErrorText>{errors.cidade}</ErrorText>}
                </FormGroup>
                
                <FormGroup style={{ flex: 1 }}>
                  <Label htmlFor="estado">UF</Label>
                  <InputWrapper hasError={!!errors.estado} isValid={estado.length === 2}>
                    <Input id="estado" type="text" value={estado} onChange={handleTextInput(setEstado, 'estado')} maxLength={2} style={{textTransform: 'uppercase'}}/>
                    <IconContainer><StatusIcon hasError={!!errors.estado} isValid={estado.length === 2} /></IconContainer>
                  </InputWrapper>
                  {errors.estado && <ErrorText>{errors.estado}</ErrorText>}
                </FormGroup>
              </Row>

              {/* --- CHECKBOX DE TERMOS E POLÍTICA (NOVO) --- */}
              <CheckboxWrapper>
                <HiddenCheckbox 
                    checked={termsAccepted}
                    onChange={handleCheckboxChange}
                />
                <StyledCheckbox checked={termsAccepted} hasError={!!errors.terms} />
                <LabelText>
                    Aceito os <Link to="/termos-de-uso" target="_blank">Termos de uso</Link> e a <Link to="/politica-de-privacidade" target="_blank">Política de privacidade</Link>.
                </LabelText>
              </CheckboxWrapper>
              {errors.terms && <ErrorText style={{marginTop: '-5px', marginBottom: '10px'}}>{errors.terms}</ErrorText>}

              <Button 
                type="submit" 
                style={{ width: '100%', marginTop: '0.5rem' }} 
                disabled={isLoading}
              >
                {isLoading ? 'Salvando...' : 'Ativar Minha Conta'}
              </Button>
            </form>

            {mensagem.text && (
              <Message type={mensagem.type} style={{ marginTop: '1.5rem' }}>
                {mensagem.text}
              </Message>
            )}
          </AuthFormCard>
        </AuthPageContainer>
      </ContentWrapper>

      <Footer />
    </PageWrapper>
  );
};

export default CompletarCadastroPage;